import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BadgeEuro,
  Bookmark,
  CarFront,
  Percent,
  Plug,
  Target,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import {
  KpiCard,
  NotConfiguredState,
  Panel,
  StatusBadge,
} from "@/components/common/Primitives";
import { OpportunityTable } from "@/components/OpportunityTable";
import { api, apiError } from "@/lib/api";
import { eur, num, pct } from "@/lib/format";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get("/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => setError(apiError(e)));
  }, []);

  const kpis = data?.kpis;
  const liveProviders = (data?.providers || []).filter((p) => p.live_data && p.enabled);
  const notConfigured = liveProviders.filter((p) => p.status !== "CONNECTED");

  return (
    <AppShell
      title="Dashboard"
      subtitle="Handelskansen voor import naar Nederland — gerangschikt op Deal Score"
      freshness={[{ label: "Dashboard gegenereerd", value: data?.generated_at }]}
      actions={
        <>
          <Link
            to="/nieuwe-scan"
            data-testid="dashboard-new-scan"
            className="rounded-sm bg-blue-600 px-3 py-1.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-blue-700"
          >
            Markt opnieuw scannen
          </Link>
          <Link
            to="/integraties"
            className="rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
          >
            Integraties
          </Link>
        </>
      }
    >
      {error && (
        <div className="mb-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
        <KpiCard
          label="Nieuwe auto's vandaag"
          value={num(kpis?.new_today ?? 0)}
          icon={CarFront}
          testid="kpi-new-today"
        />
        <KpiCard
          label="Interessante kansen"
          value={num(kpis?.opportunities ?? 0)}
          sub={`Drempel: score ≥ ${data?.thresholds?.min_deal_score ?? 65}`}
          tone={kpis?.opportunities ? "positive" : "muted"}
          icon={Target}
          testid="kpi-opportunities"
        />
        <KpiCard
          label="Potentiële totale marge"
          value={eur(kpis?.total_potential_contribution ?? 0)}
          sub="Som handelsbijdrage van kansen"
          tone={kpis?.total_potential_contribution ? "positive" : "muted"}
          icon={BadgeEuro}
          testid="kpi-total-margin"
        />
        <KpiCard
          label="Gemiddelde ROI"
          value={pct(kpis?.average_roi_pct, { fallback: "—" })}
          icon={Percent}
          testid="kpi-average-roi"
        />
        <KpiCard
          label="Hoogste Deal Score"
          value={kpis?.highest_deal_score ?? "—"}
          icon={TrendingUp}
          testid="kpi-highest-score"
        />
        <KpiCard
          label="Op watchlist"
          value={num(kpis?.watchlist_count ?? 0)}
          icon={Bookmark}
          testid="kpi-watchlist"
        />
        <KpiCard
          label="Prijsverlagingen"
          value={num(kpis?.price_reductions ?? 0)}
          icon={TrendingDown}
          testid="kpi-price-reductions"
        />
        <KpiCard
          label="Data / API status"
          value={kpis?.data_status?.status === "CONNECTED" ? "Live" : "Geen live data"}
          sub={`${kpis?.data_status?.connected ?? 0} van ${kpis?.data_status?.live_sources ?? 0} live bronnen verbonden`}
          tone={kpis?.data_status?.status === "CONNECTED" ? "positive" : "danger"}
          icon={Plug}
          testid="kpi-data-status"
        />
      </div>

      {notConfigured.length > 0 && (
        <div className="mt-3">
          <NotConfiguredState providers={notConfigured} testid="dashboard-not-configured" />
        </div>
      )}

      <div className="mt-3">
        <Panel
          title="Top handelskansen"
          subtitle={`${data?.analysed_total ?? 0} geanalyseerde advertenties — gesorteerd op Deal Score`}
          testid="top-opportunities-panel"
          actions={
            <Link
              to="/handelskansen"
              className="text-[11px] font-semibold text-blue-700 hover:underline"
              data-testid="view-all-opportunities"
            >
              Alle handelskansen →
            </Link>
          }
        >
          {(data?.top_opportunities || []).length === 0 ? (
            <div className="px-4 py-8 text-center">
              <AlertTriangle className="mx-auto h-5 w-5 text-slate-400" />
              <p className="mt-2 text-[13px] font-semibold text-slate-700">
                Nog geen geanalyseerde voertuigen
              </p>
              <p className="mx-auto mt-1 max-w-lg text-[12px] text-slate-500">
                Zodra een geautoriseerde AutoScout24-koppeling actief is, vullen scans deze tabel met
                echte advertenties. Er wordt geen demo- of fictieve data gegenereerd.
              </p>
            </div>
          ) : (
            <OpportunityTable rows={data.top_opportunities} compact testid="dashboard-table" />
          )}
        </Panel>
      </div>

      <div className="mt-3 grid gap-2 lg:grid-cols-2">
        <Panel title="Databronnen" testid="dashboard-providers">
          <div className="divide-y divide-slate-100">
            {(data?.providers || []).map((p) => (
              <div key={p.key} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0">
                  <div className="text-[12.5px] font-semibold text-slate-800">{p.label}</div>
                  <div className="truncate text-[11px] text-slate-500">{p.message || p.description}</div>
                </div>
                <StatusBadge status={p.status} label={p.status_label} testid={`provider-status-${p.key}`} />
              </div>
            ))}
          </div>
        </Panel>
        <Panel title="Actieve drempelwaarden" testid="dashboard-thresholds">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 p-3">
            {[
              ["Minimale handelsbijdrage", eur(data?.thresholds?.min_contribution)],
              ["Minimale ROI", pct(data?.thresholds?.min_roi_pct)],
              ["Minimale Deal Score", data?.thresholds?.min_deal_score ?? "—"],
              ["Minimale betrouwbaarheid", pct(data?.thresholds?.min_confidence_pct)],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between border-b border-slate-100 pb-1.5">
                <span className="label-xs">{label}</span>
                <span className="mono text-[12.5px] font-semibold text-slate-900">{value}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 px-3 py-2">
            <Link to="/instellingen" className="text-[11px] font-semibold text-blue-700 hover:underline">
              Aanpassen in Instellingen →
            </Link>
          </div>
        </Panel>
      </div>
    </AppShell>
  );
}
